import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { AlertTriangle, Clock, Users, BarChart3, TrendingUp, ChevronRight, RefreshCw, Zap } from 'lucide-react';
import api from '../../lib/api';

const RISK_COLORS = {
  CRITICAL: 'bg-red-600 text-white',
  HIGH: 'bg-orange-500 text-white',
  MEDIUM: 'bg-yellow-500 text-black',
  LOW: 'bg-slate-500 text-white',
};

const WORKLOAD_COLORS = {
  OVERLOADED: 'text-red-400',
  HIGH: 'text-orange-400',
  MODERATE: 'text-yellow-400',
  BALANCED: 'text-emerald-400',
};

export default function PredictiveStaffingPage() {
  const navigate = useNavigate();
  const [risks, setRisks] = useState(null);
  const [capacity, setCapacity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeWindow, setActiveWindow] = useState('3d');

  const fetchData = async () => {
    setLoading(true);
    try {
      const [risksRes, capRes] = await Promise.all([
        api.get('/predictive/upcoming-risks?hours=168'),
        api.get('/predictive/capacity?days=7'),
      ]);
      if (risksRes.data?.data) setRisks(risksRes.data.data);
      if (capRes.data?.data) setCapacity(capRes.data.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent" />
      </div>
    );
  }

  const currentWindow = risks?.windows?.[activeWindow] || {};
  const sessions = currentWindow.sessions || [];

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white" data-testid="predictive-staffing-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <Zap className="h-6 w-6 text-amber-400" />
              Predictive Staffing
            </h1>
            <p className="text-sm text-slate-400 mt-1">Forecast staffing risks before they happen</p>
          </div>
          <Button onClick={fetchData} className="bg-blue-600 hover:bg-blue-700" data-testid="refresh-predictions-btn">
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
        </div>

        {/* Risk Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {['24h', '3d', '7d'].map(w => {
            const win = risks?.windows?.[w] || {};
            return (
              <Card
                key={w}
                className={`bg-[#12121a] border-[#1e1e2e] cursor-pointer transition-colors ${activeWindow === w ? 'border-blue-600' : 'hover:border-slate-600'}`}
                onClick={() => setActiveWindow(w)}
                data-testid={`window-${w}`}
              >
                <CardContent className="p-4">
                  <p className="text-xs text-slate-500 uppercase">Next {w}</p>
                  <div className="flex items-baseline gap-2 mt-1">
                    <span className="text-2xl font-bold">{win.at_risk || 0}</span>
                    <span className="text-xs text-slate-500">at risk</span>
                  </div>
                  {(win.critical || 0) > 0 && (
                    <Badge className="bg-red-600 text-white text-[10px] mt-1">{win.critical} critical</Badge>
                  )}
                </CardContent>
              </Card>
            );
          })}

          {/* Capacity Card */}
          <Card className="bg-[#12121a] border-[#1e1e2e]" data-testid="capacity-card">
            <CardContent className="p-4">
              <p className="text-xs text-slate-500 uppercase">Utilization</p>
              <div className="flex items-baseline gap-2 mt-1">
                <span className="text-2xl font-bold">{capacity?.utilization_rate || 0}%</span>
              </div>
              {(capacity?.overworked_count || 0) > 0 && (
                <p className="text-[10px] text-orange-400 mt-1">{capacity.overworked_count} overworked</p>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* At-Risk Sessions List */}
          <div className="lg:col-span-2">
            <Card className="bg-[#12121a] border-[#1e1e2e]">
              <CardHeader className="pb-3">
                <CardTitle className="text-base text-slate-300 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-400" />
                  Sessions at Risk ({currentWindow.at_risk || 0})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {sessions.length === 0 ? (
                  <div className="p-8 text-center text-slate-500 text-sm">No risks detected for this window</div>
                ) : (
                  <div className="divide-y divide-[#1e1e2e]">
                    {sessions.map((s, idx) => (
                      <div
                        key={s.session_id}
                        className="px-4 py-3 hover:bg-[#1a1a25] cursor-pointer transition-colors"
                        onClick={() => navigate(`/admin/group-sessions/${s.session_id}`)}
                        data-testid={`risk-session-${idx}`}
                      >
                        <div className="flex items-center gap-2">
                          <Badge className={`${RISK_COLORS[s.risk_level]} text-[10px] px-1.5 shrink-0`}>{s.risk_level}</Badge>
                          <span className="text-sm font-medium text-slate-200 truncate flex-1">
                            {s.session_code} — {s.title || 'Untitled'}
                          </span>
                          <span className="text-xs text-slate-500 shrink-0">
                            {s.assigned_staff}/{s.required_staff} staff
                          </span>
                          <ChevronRight className="h-4 w-4 text-slate-600 shrink-0" />
                        </div>
                        <div className="flex items-center gap-3 mt-1 ml-14">
                          {s.staffing_gap > 0 && (
                            <span className="text-[10px] text-red-400 flex items-center gap-1">
                              <Users className="h-3 w-3" /> -{s.staffing_gap} staff
                            </span>
                          )}
                          <span className="text-[10px] text-slate-600">
                            <Clock className="h-3 w-3 inline mr-0.5" />
                            {s.start_datetime ? new Date(s.start_datetime).toLocaleDateString() : ''}
                          </span>
                          <span className="text-[10px] text-slate-600">Score: {s.risk_score}</span>
                        </div>
                        {s.risk_factors?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1.5 ml-14">
                            {s.risk_factors.map((f, fi) => (
                              <span key={fi} className="text-[10px] px-1.5 py-0.5 rounded bg-[#1a1a2e] text-slate-500">
                                {f.factor}: +{f.impact}
                              </span>
                            ))}
                          </div>
                        )}
                        {s.suggested_action && (
                          <p className="text-[10px] text-blue-400 mt-1 ml-14">{s.suggested_action}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Capacity Panel */}
          <div className="space-y-4">
            <Card className="bg-[#12121a] border-[#1e1e2e]">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-slate-300 flex items-center gap-2">
                  <BarChart3 className="h-4 w-4 text-blue-400" />
                  Staff Workload (7 days)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="p-2 rounded bg-[#0d0d14]">
                    <p className="text-slate-500">Total Staff</p>
                    <p className="text-lg font-bold">{capacity?.total_staff || 0}</p>
                  </div>
                  <div className="p-2 rounded bg-[#0d0d14]">
                    <p className="text-slate-500">Utilized</p>
                    <p className="text-lg font-bold">{capacity?.utilized_staff || 0}</p>
                  </div>
                </div>

                {capacity?.imbalance_detected && (
                  <div className="flex items-center gap-1.5 px-2 py-1.5 rounded bg-orange-900/20 border border-orange-900/30 text-[10px] text-orange-400">
                    <TrendingUp className="h-3 w-3" />
                    Workload imbalance detected
                  </div>
                )}

                <div className="space-y-1.5">
                  {(capacity?.staff_workloads || []).slice(0, 8).map((sw, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-xs">
                      <span className="text-slate-400 truncate flex-1">{sw.staff_name}</span>
                      <span className="text-slate-500 w-6 text-right">{sw.total_assignments}</span>
                      <span className={`text-[10px] w-20 text-right ${WORKLOAD_COLORS[sw.workload_status]}`}>
                        {sw.workload_status}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
